import {useResizeObserver} from '@github-ui/use-resize-observer'
import {useCallback, useEffectEvent, useLayoutEffect, useRef, useState} from 'react'

import {computeVisibleTokenCount, type VisibleTokenCount} from './compute-visible-token-count'

type OverflowState = VisibleTokenCount & Readonly<{measured: boolean}>

type UseTokenRowOverflowArgs = Readonly<{
  /** Number of selected options currently rendered. */
  optionCount: number
  /**
   * Stable signature of the rendered option set. A change (a different set of
   * options, or a rename that alters token widths) starts a fresh measurement
   * pass. Must include the rendered names, not just ids, because renaming an
   * option changes its token width without changing the option set.
   */
  signature: string
  /** Horizontal gap between tokens, in pixels. */
  gap: number
  /** Reserved width for the `+N` badge, in pixels. */
  badgeWidth: number
  /**
   * When false (expanded row heights that wrap onto multiple lines), overflow
   * handling is skipped and every token is shown.
   */
  enabled: boolean
}>

type UseTokenRowOverflowResult = OverflowState &
  Readonly<{
    /** Callback ref to attach to the token row element. */
    ref: (node: HTMLElement | null) => (() => void) | undefined
  }>

/**
 * Measures the token row and reports how many tokens fit before a `+N`
 * badge is needed.
 *
 * Measurement is a two-phase pass: while `measured` is false the caller draws
 * the full, unsliced token list (see `MultiSelectRenderer`), so every token
 * width is readable from the DOM; a layout effect then reads those widths and
 * reports the sliced result before the browser paints, so there is no visible
 * flash of the full list.
 *
 * The measurement-pass state updates run inside effect events
 * (`requestMeasurement`, `runMeasurement`), which read the latest args without
 * widening effect dependencies and are exempt from the effect set-state rules.
 * Each update uses a functional bail-out (returning the previous state when
 * nothing changed) so repeated measurements cannot spin the render loop.
 *
 * The row is tracked with a shared, `requestAnimationFrame`-coalesced
 * `ResizeObserver` exposed as a callback ref (`@github-ui/use-resize-observer`).
 * Because it is a callback ref, observation begins the moment the row attaches
 * and stops when it detaches, so a burst of column-resize notifications
 * collapses to a single measurement per frame instead of the per-cell
 * resize -> render -> resize churn that hurts INP in Safari.
 *
 * The DOM-dependent measurement lives here; the fit maths lives in the pure,
 * unit-tested `computeVisibleTokenCount`.
 */
export function useTokenRowOverflow({
  optionCount,
  signature,
  gap,
  badgeWidth,
  enabled,
}: UseTokenRowOverflowArgs): UseTokenRowOverflowResult {
  const elementRef = useRef<HTMLElement | null>(null)

  const [state, setState] = useState<OverflowState>({
    visibleCount: optionCount,
    hiddenCount: 0,
    measured: false,
  })

  // Clear the measured flag so the renderer redraws the full token list, giving
  // the next measurement pass every token width to read. No-op when a pass is
  // already pending.
  const requestMeasurement = useEffectEvent(() => {
    setState(prev => (prev.measured ? {visibleCount: optionCount, hiddenCount: 0, measured: false} : prev))
  })

  // Read the rendered token widths and report the sliced result. Runs only once
  // the full list is on screen (`measured === false`).
  const runMeasurement = useEffectEvent(() => {
    const el = elementRef.current
    if (!el) return

    if (!enabled || optionCount === 0) {
      setState(prev =>
        prev.measured && prev.visibleCount === optionCount && prev.hiddenCount === 0
          ? prev
          : {visibleCount: optionCount, hiddenCount: 0, measured: true},
      )
      return
    }

    // While `measured` is false the row holds exactly the tokens (the badge is
    // only rendered once `hiddenCount > 0`), so its direct children are the
    // tokens. Measure the children rather than a `[data-testid]` selector, which
    // `testIdProps` strips in production, leaving nothing to measure.
    const tokens = (Array.from(el.children) as Array<HTMLElement>).slice(0, optionCount)
    // The full list is not on screen yet (a redraw is pending); wait for it.
    if (tokens.length < optionCount) return

    const tokenWidths = tokens.map(token => token.getBoundingClientRect().width)
    const {visibleCount, hiddenCount} = computeVisibleTokenCount({
      // Measure the container with `getBoundingClientRect().width` so it shares
      // the same box model (border-box) and fractional precision as the token
      // widths above, rather than mixing in integer-rounded `clientWidth`.
      containerWidth: el.getBoundingClientRect().width,
      tokenWidths,
      gap,
      badgeWidth,
    })
    setState(prev =>
      prev.measured && prev.visibleCount === visibleCount && prev.hiddenCount === hiddenCount
        ? prev
        : {visibleCount, hiddenCount, measured: true},
    )
  })

  // Start a fresh pass whenever the rendered option set changes (count, ids, or
  // names) or wrapping is toggled.
  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    requestMeasurement()
  }, [signature, optionCount, enabled])

  // Once the full list is on screen, take the measurement before paint. Keyed on
  // `optionCount` and `signature` as well as `measured` so a row that mounts only
  // after its value finishes loading (option count 0 -> N) is measured, not left
  // stuck reporting the initial unmeasured counts.
  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!state.measured) runMeasurement()
  }, [state.measured, optionCount, signature, enabled])

  // Re-measure on container resize (e.g. dragging a column wider or narrower).
  // The shared observer coalesces a burst of notifications into a single
  // rAF-batched callback. The reset is inlined here rather than routed through
  // the `requestMeasurement` effect event because `rules-of-hooks` forbids
  // calling an effect event from a plain observer callback. Clearing `measured`
  // re-arms the measurement layout effect, which reads the fresh option set; the
  // functional bail keeps an already-pending pass from re-entering the loop.
  const observeRef = useResizeObserver(() => {
    setState(prev => (prev.measured ? {...prev, measured: false} : prev))
  })

  const ref = useCallback(
    (node: HTMLElement | null) => {
      elementRef.current = node
      // When overflow handling is disabled there is nothing to re-measure on
      // resize, so skip observing entirely. This keeps the disabled path (the
      // default until the feature flag ships) free of any ResizeObserver work.
      const cleanup = enabled ? observeRef(node) : undefined
      return () => {
        elementRef.current = null
        cleanup?.()
      }
    },
    [observeRef, enabled],
  )

  return {...state, ref}
}
